import React from 'react'
import { useAppHeaderStyles } from './app-header.styles'
import { AppBar, Toolbar, Menu, MenuItem } from '@material-ui/core'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import { Link, NavLink } from 'react-router-dom'
import { AppHeaderRightMenu } from './app-header-right-menu'
import { AppHeaderLoadingIndicator } from './app-header-loading-indicator'
import { useSelector } from 'react-redux'
import { selectCheckedAuth, selectUser } from '../../store/auth/selectors'
import { AppHeaderLogo } from './app-header-logo'
import { AppHeaderNotification } from '../app-header-notifications'
import { AppHeaderDrawer } from './app-header-drawer'
import { useAuthRedirect } from '../../hooks/use-auth-redirect'
import { useTranslation } from 'react-i18next'
import {
  useMenuItems,
  MenuItemWithLabel,
  MenuLinkItemWithLabel,
} from './app-header.hook'
import { BundleNotificationsDrawer } from '../../pages/notifications-page/bundle-notifications-drawer'

export const AppHeader: React.FC = (): React.ReactElement => {
  const classes = useAppHeaderStyles()
  const isLoading = !useSelector(selectCheckedAuth)
  const user = useSelector(selectUser)
  const { authRedirectHandle } = useAuthRedirect()
  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const menuItems = useMenuItems(t, user, authRedirectHandle)

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const renderMainMenuItem = (item: MenuLinkItemWithLabel) => {
    const className = classes.menuItem
    const commonProps = {
      className,
      onClick: item.onClick,
    }

    if (item.isExternal) {
      return (
        <a
          key={item.to}
          href={item.to}
          target="_blank"
          rel="noreferrer"
          {...commonProps}
        >
          {item.label}
        </a>
      )
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        {...commonProps}
        className={({ isActive }) =>
          isActive
            ? [classes.active, classes.menuItem].join(' ')
            : classes.menuItem
        }
      >
        {item.label}
      </NavLink>
    )
  }

  const renderExpandableMenuItem = (item: MenuItemWithLabel) => {
    const className = item.isHiddenOnMedium
      ? [classes.menuItem, classes.menuItemMediumHide].join(' ')
      : classes.menuItem

    if (item.isExternal) {
      return (
        <a
          key={item.href}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          className={className}
        >
          {item.label}
        </a>
      )
    }

    return (
      <NavLink
        key={item.href}
        to={item.href}
        className={({ isActive }) =>
          isActive
            ? [classes.active, classes.menuItem].join(' ')
            : classes.menuItem
        }
      >
        {item.label}
      </NavLink>
    )
  }

  const renderDropdownMenuItem = (item: MenuItemWithLabel) => (
    <MenuItem key={item.href} onClick={handleMenuClose}>
      {item.isExternal ? (
        <a
          className={classes.menuItemExpandable}
          href={item.href}
          target="_blank"
          rel="noreferrer"
        >
          {item.label}
        </a>
      ) : (
        <Link className={classes.menuItemExpandable} to={item.href}>
          {item.label}
        </Link>
      )}
    </MenuItem>
  )

  return (
    <AppBar position="static" className={classes.appBar}>
      {isLoading && <AppHeaderLoadingIndicator />}
      <Toolbar>
        <div className={classes.menuWrapper}>
          {!isLoading && (
            <div className={classes.menuBlockWrapper}>
              <AppHeaderLogo />
              <nav className={classes.menuBlockLeft}>
                {menuItems.mainItems.map(renderMainMenuItem)}
                {menuItems.expandableItems.map(renderExpandableMenuItem)}
                <div className={classes.menuExpandable}>
                  <span
                    aria-controls="simple-menu"
                    aria-haspopup="true"
                    onClick={handleMenuClick}
                    className={classes.dropdownMenuIcon}
                  >
                    <MoreHorizIcon />
                  </span>
                  <Menu
                    id="simple-menu"
                    className={classes.dropdownMenu}
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    {menuItems.expandableItems.map(renderDropdownMenuItem)}
                  </Menu>
                </div>
              </nav>

              <div className={classes.menuBlockRight}>
                {!!user.userId && <AppHeaderNotification />}
                <AppHeaderRightMenu />
                <AppHeaderDrawer />
              </div>
            </div>
          )}
        </div>
      </Toolbar>
      <BundleNotificationsDrawer />
    </AppBar>
  )
}
